import React from 'react'
import { ColDef, ColGroupDef, GridOptions, ICellEditorParams } from 'ag-grid-community'
import { Locale, Translation } from 'locale'
import { Product, ProductSector } from 'api'
import { AgGridWrapper, gridOptionsBase, groupingRowColDef, TypedValueGetterParams } from 'view/components/datagrid'
import { OnOffButtons, OnOffGroupButtons } from 'view/components/buttons'

export interface OnOffProductsPanelGridRow {
  productSector: ProductSector
  product: Product
  status: boolean
}

export interface ProductsPanelGridProps {
  rowData: OnOffProductsPanelGridRow[]
  disabled?: boolean
  onUpdate: (productSectorIds: string[], status: boolean) => void
}

const noProductsPlaceholder = () => <div>{Locale.translate(Translation.SC_NO_PRODUCTS_AVAILABLE)}</div>

const cellRenderer = React.forwardRef((params: ICellEditorParams, ref: React.Ref<any>) => {
  const allLeafChildren = params.node.allLeafChildren ?? []

  return params.node.group ? (
    <OnOffGroupButtons
      ref={ref}
      on={allLeafChildren.map(node => node.data.status)}
      onLabel={Translation.UC_ON}
      offLabel={Translation.UC_OFF}
      onChange={on => {
        return params.context.onUpdateProductStatus(
          allLeafChildren.map(node => {
            return node.data.productSector.id
          }),
          on
        )
      }}
      disabled={params.context.disabled}
    />
  ) : (
    <OnOffButtons
      ref={ref}
      on={params.node.data.status}
      onLabel={Translation.UC_ON}
      offLabel={Translation.UC_OFF}
      onChange={on => params.context.onUpdateProductStatus([params.node.data.productSector.id], on)}
      disabled={params.context.disabled}
    />
  )
})

const columnDefs: (ColDef | ColGroupDef)[] = [
  {
    ...groupingRowColDef,
    headerName: Locale.translate(Translation.TC_PRODUCT),
    width: 100,
    showRowGroup: 'PRODUCT_NAME',
    headerComponent: undefined,
    pinned: false,
    resizable: true,
    hide: false
  },
  {
    colId: 'PRODUCT_NAME',
    valueGetter: (params: TypedValueGetterParams<OnOffProductsPanelGridRow>) => params.data && params.data.product.name,
    rowGroup: true,
    hide: true,
    resizable: true
  },
  {
    colId: 'PRODUCT_SECTOR_NAME',
    valueGetter: (params: TypedValueGetterParams<OnOffProductsPanelGridRow>) => params.data && params.data.productSector.name,
    headerName: Locale.translate(Translation.TC_SECTOR),
    suppressMovable: true,
    resizable: true,
    width: 140,
    minWidth: 140
  },
  {
    colId: 'STATUS',
    valueGetter: (params: TypedValueGetterParams<OnOffProductsPanelGridRow>) => params.data && params.data.status,
    headerName: Locale.translate(Translation.TC_STATUS),
    width: 100,
    suppressMovable: true,
    resizable: true,
    sortable: false,
    aggFunc: () => NaN,
    cellRenderer
  }
]

const gridOptions: GridOptions = {
  ...gridOptionsBase,
  groupDefaultExpanded: 0,
  getRowId: ({ data }) => data.productSector.id
}

export class OnOffProductsPanelGrid extends React.Component<ProductsPanelGridProps> {
  render() {
    return (
      <AgGridWrapper
        className="datagrid-borderless"
        gridOptions={gridOptions}
        columnDefs={columnDefs}
        rowData={this.props.rowData}
        context={{
          onUpdateProductStatus: this.onUpdateProductStatus,
          disabled: this.props.disabled
        }}
        placeholder={noProductsPlaceholder}
      />
    )
  }

  onUpdateProductStatus = (productSectors: string[], status: boolean) => {
    this.props.onUpdate(productSectors, status)
  }
}
